import GeoMoverDB from '@/db/indexeddb'

/* eslint-disable no-console */

/**
 * Settings for the app.
 *
 * Almost all settings must not only be saved to VueX state, but also
 * to the GeoMoverDB.
 */
export default {
  setDbReady ({ commit }, bool) {
    commit('SET_DB_LOADED', bool)
  },
  //
  // ----- Settings fetched from DB
  //       They update VueX on startUp
  //
  async initSettings ({ commit, dispatch, state }, data) {
    await GeoMoverDB.getAllSettings()
      .then(data => {
        // Global App Permissions
        commit('SET_TRACKING_ALLOWED', data.trackingAllowed)
        commit('SET_SENSORS_ALLOWED', data.sensorsAllowed)
        // Browser API Permissions
        commit('SET_GEOLOCATION_PERMISSION_STATUS', data.geolocationPermissionStatus)
        commit('SET_GYROSCOPE_PERMISSION_STATUS', data.gyroscopePermissionStatus)
        // Geolocation specifics
        commit('SET_ENABLE_HIGH_ACCURACY', data.enableHighAccuracy)
        commit('SET_TIMEOUT', data.timeout)
        commit('SET_MAXIMUM_AGE', data.maximumAge)
        // Geolocation Recording Constraints
        commit('SET_USE_MIN_SPEED_THRESHOLD', data.useMinSpeedThreshold)
        commit('SET_MIN_SPEED_THRESHOLD', data.minSpeedThreshold)
        commit('SET_USE_RECORDING_TIME_INTERVAL', data.useRecordingTimeInterval)
        commit('SET_RECORDING_TIME_INTERVAL', data.recordingTimeInterval)
        // Reverse Geocoding
        commit('SET_ADDRESS_SERVICE', data.addressService)
        // Gyroscope API
        commit('SET_USE_PEDOMETER', data.usePedometer)
        // Screen Wake-lock API
        commit('SET_SCREEN_WAKE_LOCK_AVAILABILITY', data.screenWakeLockAvailability)
        // Notification
        commit('SET_SHOW_NOTIFICATION_WHEN_NOT_MOVING', data.showNotificationWhenNotMoving)
        //
        // This mutation lives in "activity-types"
        commit('activityTypes/SET_CURRENT_ACTIVITY_TYPE', data.currentActivityType, { root: true })
        // openlayers
        commit('SET_ZOOM_LEVEL_NEAR', data.zoomLevelNear)
        commit('SET_ZOOM_LEVEL_FAR', data.zoomLevelFar)
        // Sidebar sections
        commit('SET_MAX_MAP_HOSTS_PER_PAGE', data.maxMapHostsPerPage)
        commit('SET_MAX_NOTES_PER_PAGE', data.maxNotesPerPage)
        commit('SET_MAX_TRACKED_ROUTES_PER_PAGE', data.maxTrackedRoutesPerPage)
        // Debugging
        commit('SET_DEBUGGING', data.debugging)
        commit('SET_CONSOLE', data.console)
      })
      .catch(error => {
        console.error('VUEX: initSettings error', error.id + ': ' + error.message)
      })
      .finally(() => {
        // console.warn('STORE SETTINGS ACTION, after "Init()": Do Web API checks here')
        // Check WEB APIs
        dispatch('initialGeolocationPermissionCheck')
        // dispatch('initialGyroscopePermissionCheck')
        dispatch('initialScreenWakeLockAvailabilityCheck')
      })
  },
  //
  // ----- Connection to the DB
  //
  updateOneSetting ({ commit, data }, payload) {
    const result = GeoMoverDB.updateOneSetting(payload)
      .then(success => {
        // console.info('updateOneSetting successful')
      })
      .catch(error => {
        console.error('updateOneSetting error', error)
      })
      .finally(() => {
        // console.log('Finally finished updateOneSetting')
      })
  },
  //
  // -----------------------------------------------
  //    Set individual params and save the to DB
  // -----------------------------------------------
  //
  // ----- Global permissions
  //
  setTrackingAllowed ({ commit, dispatch }, bool) {
    commit('SET_TRACKING_ALLOWED', bool)
    dispatch('updateOneSetting', { feature: 'trackingAllowed', value: bool })
  },
  setSensorsAllowed ({ commit, dispatch }, bool) {
    commit('SET_SENSORS_ALLOWED', bool)
    dispatch('updateOneSetting', { feature: 'sensorsAllowed', value: bool })
  },
  //
  // ----- Geolocation Permission
  //
  /**
   * "initialGeolocationPermissionCheck" checks the permission status of
   * the navigator's geolocation on startup. It is called from "init" from
   * the first "router-loaded" Vue component (most likely "NewActivity"). It
   * has a different purpose then "handleGeolocationPermission", so  don't
   * get confused, just keep them both!
   */
  initialGeolocationPermissionCheck ({ dispatch }) {
    // console.info('store.settings.actions - initialGeolocationPermissionCheck')
    // console.log('navigator =', navigator)

    navigator.permissions.query({ name: 'geolocation' })
      .then(geolocationPermissionStatus => {
        // console.log('geolocationPermissionStatus =', geolocationPermissionStatus)
        // geolocationPermissionStatus.state === 'granted'
        // geolocationPermissionStatus.state === 'prompt'
        // geolocationPermissionStatus.state === 'denied'
        dispatch('reportGeolocationPermissionStatus', geolocationPermissionStatus.state)
      })
  },
  initialGyroscopePermissionCheck ({ dispatch }) {
    navigator.permissions.query({ name: 'gyroscope' })
      .then(gyroscopePermissionStatus => {
        dispatch('reportGyroscopePermissionStatus', gyroscopePermissionStatus.state)
      })
  },
  initialScreenWakeLockAvailabilityCheck ({ dispatch }) {
    'wakeLock' in navigator
      ? dispatch('reportScreenWakeLockAvailability', true)
      : dispatch('reportScreenWakeLockAvailability', true)
  },
  // // further permission-aware APIs
  // Clipboard
  // Notification
  // MIDI
  // Push API
  /**
   * This function is called from the "Grant Geoloction" button in "NewActivity",
   * when the permission of Geolocation does not return "granted" from VueX on
   * startup. It has a different purpose then "initialGeolocationPermissionCheck",
   * so don't confuse them and keep them both!
   */
  handleGeolocationPermission ({ dispatch }) {
    // console.info('store.settings.actions - handleGeolocationPermission: ')

    navigator.permissions.query({ name: 'geolocation' })
      .then(geolocationPermissionStatus => {
        // console.log('geolocationPermissionStatus =', geolocationPermissionStatus)

        if (geolocationPermissionStatus.state === 'prompt') {
          // console.log('> prompt (invoke geolocation.getCurrentPosition to make the browser display the promt)')
          navigator.geolocation.getCurrentPosition(() => { })
        }
        if (geolocationPermissionStatus.state === 'denied') {
          console.warn('handleGeolocationPermission: geolocationPermissionStatus denied (permission denied; ask again? (Does this work?)')
          navigator.geolocation.getCurrentPosition(() => { })
        }

        // Invoke the change manually
        dispatch('reportGeolocationPermissionStatus', geolocationPermissionStatus.state)

        geolocationPermissionStatus.onchange = () => {
          // console.log('geolocationPermissionStatus.onChange invoked: geolocationPermissionStatus.state = ' + geolocationPermissionStatus.state)
          dispatch('reportGeolocationPermissionStatus', geolocationPermissionStatus.state)
        }
      })
  },
  reportGeolocationPermissionStatus ({ commit, dispatch }, status) {
    // console.info('store.settings.actions - reportGeolocationPermissionStatus')
    commit('SET_GEOLOCATION_PERMISSION_STATUS', status)
    dispatch('updateOneSetting', { feature: 'geolocationPermissionStatus', value: status })
  },
  reportGyroscopePermissionStatus ({ commit, dispatch }, status) {
    // console.info('store.settings.actions - reportGeolocationPermissionStatus')
    commit('SET_GYROSCOPE_PERMISSION_STATUS', status)
    dispatch('updateOneSetting', { feature: 'gyroscopePermissionStatus', value: status })
  },
  reportScreenWakeLockAvailability ({ commit, dispatch }, status) {
    // console.info('store.settings.actions - reportGeolocationPermissionStatus')
    commit('SET_SCREEN_WAKE_LOCK_AVAILABILITY', status)
    dispatch('updateOneSetting', { feature: 'screenWakeLockAvailability', value: status })
  },
  //
  // ----- Geolocation options
  //
  setEnableHighAccuracy ({ commit, dispatch }, bool) {
    commit('SET_ENABLE_HIGH_ACCURACY', bool)
    dispatch('updateOneSetting', { feature: 'enableHighAccuracy', value: bool })
  },
  setMaximumAge ({ commit, dispatch }, int) {
    commit('SET_MAXIMUM_AGE', parseInt(int))
    dispatch('updateOneSetting', { feature: 'maximumAge', value: parseInt(int) })
  },
  setTimeout ({ commit, dispatch }, int) {
    commit('SET_TIMEOUT', parseInt(int))
    dispatch('updateOneSetting', { feature: 'timeout', value: parseInt(int) })
  },
  //
  // ----- Geolocation Recording constrains
  //
  setUseMinSpeedThreshold ({ commit, dispatch }, bool) {
    commit('SET_USE_MIN_SPEED_THRESHOLD', bool)
    dispatch('updateOneSetting', { feature: 'useMinSpeedThreshold', value: bool })
  },
  setMinSpeedThreshold ({ commit, dispatch }, float) {
    commit('SET_MIN_SPEED_THRESHOLD', parseFloat(float))
    dispatch('updateOneSetting', { feature: 'minSpeedThreshold', value: parseFloat(float) })
  },
  setUseRecordingTimeInterval ({ commit, dispatch }, bool) {
    commit('SET_USE_RECORDING_TIME_INTERVAL', bool)
    dispatch('updateOneSetting', { feature: 'useRecordingTimeInterval', value: bool })
  },
  setRecordingTimeInterval ({ commit, dispatch }, int) {
    commit('SET_RECORDING_TIME_INTERVAL', parseInt(int))
    dispatch('updateOneSetting', { feature: 'recordingTimeInterval', value: parseInt(int) })
  },
  //
  // Reverse Geocoding
  //
  setAddressService ({ commit, dispatch }, payload) {
    commit('SET_ADDRESS_SERVICE', payload)
    dispatch('updateOneSetting', { feature: 'addressService', value: payload })
  },
  //
  // ----- Device settings: Screen WakeLock Web API
  //
  setScreenWakeLockAvailability ({ commit, dispatch }, bool) {
    commit('SET_SCREEN_WAKE_LOCK_AVAILABILITY', bool)
    dispatch('updateOneSetting', { feature: 'screenWakeLockAvailability', value: bool })
  },
  //
  // ------ Sensores: Gyroscope
  //
  setUsePedometer ({ commit, dispatch }, bool) {
    commit('SET_USE_PEDOMETER', bool)
    dispatch('updateOneSetting', { feature: 'usePedometer', value: bool })
  },
  //
  // ----- Notification when not moving
  //
  setShowNotificationWhenNotMoving ({ commit, dispatch }, bool) {
    commit('SET_SHOW_NOTIFICATION_WHEN_NOT_MOVING', bool)
    dispatch('updateOneSetting', { feature: 'showNotificationWhenNotMoving', value: bool })
  },
  //
  // ----- Zoom (OpenLayers)
  //
  setZoomLevelNear ({ commit, dispatch }, int) {
    commit('SET_ZOOM_LEVEL_NEAR', parseInt(int))
    dispatch('updateOneSetting', { feature: 'zoomLevelNear', value: parseInt(int) })
  },
  setZoomLevelFar ({ commit, dispatch }, int) {
    commit('SET_ZOOM_LEVEL_FAR', parseInt(int))
    dispatch('updateOneSetting', { feature: 'zoomLevelFar', value: parseInt(int) })
  },
  setZoomLevelManual ({ commit }, int) {
    commit('SET_ZOOM_LEVEL_MANUAL', parseInt(int))
    // This one is not and should not be stored / tracked in IDB!
  },
  //
  // ----- Pagination (Sidebar sections)
  //
  setMaxMapHostsPerPage ({ commit, dispatch }, int) {
    commit('SET_MAX_MAP_HOSTS_PER_PAGE', parseInt(int))
    dispatch('updateOneSetting', { feature: 'maxMapHostsPerPage', value: parseInt(int) })
  },
  setMaxNotesPerPage ({ commit, dispatch }, int) {
    commit('SET_MAX_NOTES_PER_PAGE', parseInt(int))
    dispatch('updateOneSetting', { feature: 'maxNotesPerPage', value: parseInt(int) })
  },
  setMaxTrackedRoutesPerPage ({ commit, dispatch }, int) {
    commit('SET_MAX_TRACKED_ROUTES_PER_PAGE', parseInt(int))
    dispatch('updateOneSetting', { feature: 'maxTrackedRoutesPerPage', value: parseInt(int) })
  },
  //
  // ----- Console, Debugging, log text
  //
  setConsole ({ commit, dispatch }, bool) {
    commit('SET_CONSOLE', bool)
    dispatch('updateOneSetting', { feature: 'console', value: bool })
  },
  setDebugging ({ commit, dispatch }, bool) {
    commit('SET_DEBUGGING', bool)
    dispatch('updateOneSetting', { feature: 'debugging', value: bool })
  },
  setLog ({ state, commit }, payload) {
    if (state.debugging) {
      commit('SET_LOG_TXT', payload)
    }
  },
  resetLog ({ commit }) {
    commit('RESET_LOG')
  }
}
